import { SYSTEMS } from './systems.consts';

export const GENETIC_TEST_CATEGORIES = {
  POSTNATAL: 'Μεταγεννητικά',
  PRENATAL: 'Προγεννητικά',
  MITOCHONDRIAL: 'Μιτοχονδριακά',
  PREIMPLANTATION: 'Προεμφυτευτικός Γενετικός Έλεγχος',
} as const;

// Note: Using MMG-specific codes as placeholders - replace with actual LOINC codes if available
export const GENETIC_TESTS = {
  // Postnatal tests
  KARYOTYPE_BLOOD: {
    code: '11551-0',
    display: 'Καρυότυπος περιφερικού αίματος',
    category: 'POSTNATAL',
  },
  KARYOTYPE_COUPLE: {
    code: '11552-8',
    display: 'Καρυότυπος περιφερικού αίματος ζεύγους',
    category: 'POSTNATAL',
  },
  KARYOTYPE_SPECIAL: {
    code: '11553-6',
    display: 'Καρυότυπος με ειδικές τεχνικές (σύνδρομα ευθραυστότητας)',
    category: 'POSTNATAL',
  },
  WES: {
    code: '94186-4',
    display: 'Whole Exome Sequencing (WES)',
    category: 'POSTNATAL',
  },
  WGS: {
    code: '82943-2',
    display: 'Whole Genome Sequencing (WGS)',
    category: 'POSTNATAL',
  },
  BIOINFORMATICS_ANALYSIS: {
    code: '81479-2',
    display: 'Βιοπληροφορική ανάλυση δεδομένων WES/WGS',
    category: 'POSTNATAL',
  },
  EXPANDED_CARRIER: {
    code: '81466-9',
    display: 'Διευρυμένος έλεγχος φορείας (Expanded Carrier Screening)',
    category: 'POSTNATAL',
  },
  ARRAY_CGH: {
    code: '94474-4',
    display: 'Μοριακός καρυότυπος (arrayCGH)',
    category: 'POSTNATAL',
  },
  CUSTOM_VARIANT: {
    code: '69548-6',
    display: 'Στοχευμένος έλεγχος γνωστής παραλλαγής (Custom)',
    category: 'POSTNATAL',
  },
  NEUROCUTANEOUS: {
    code: '55232-3',
    display: 'Μοριακός έλεγχος νευροδερματικών νοσημάτων (NF1, Noonan, TS κλπ)',
    category: 'POSTNATAL',
  },
  CFTR_COMMON: {
    code: '21416-3',
    display:
      'Κυστική Ίνωση (CFTR) – 90% των παραλλαγών του Ελληνικού πληθυσμού',
    category: 'POSTNATAL',
  },
  CFTR_FULL: {
    code: '21412-2',
    display: 'Κυστική Ίνωση (CFTR) – Πλήρης έλεγχος',
    category: 'POSTNATAL',
  },
  WILSON_COMMON: {
    code: '42839-1',
    display: 'Νόσος Wilson – 86% των παραλλαγών του Ελληνικού πληθυσμου',
    category: 'POSTNATAL',
  },
  WILSON_FULL: {
    code: '42840-9',
    display: 'Νόσος Wilson – Πλήρης έλεγχος',
    category: 'POSTNATAL',
  },
  MEDITERRANEAN_ANEMIA: {
    code: '54371-3',
    display: 'Μεσογειακή αναιμία',
    category: 'POSTNATAL',
  },
  BLACKFAN_DIAMOND_RPS19: {
    code: '55437-8',
    display: 'Αναιμία Blackfan-Diamond (RPS19)',
    category: 'POSTNATAL',
  },
  BLACKFAN_DIAMOND_RPL11: {
    code: '55438-6',
    display: 'Αναιμία Blackfan-Diamond (RPL11)',
    category: 'POSTNATAL',
  },
  BLACKFAN_DIAMOND_RPL5: {
    code: '55439-4',
    display: 'Αναιμία Blackfan-Diamond (RPL5)',
    category: 'POSTNATAL',
  },
  FANCONI_ANEMIA: {
    code: '50619-1',
    display: 'Αναιμία Fanconi',
    category: 'POSTNATAL',
  },
  SCHWACHMAN_DIAMOND: {
    code: '62370-3',
    display: 'Σύνδρομο Schwachman Diamond',
    category: 'POSTNATAL',
  },
  DMD_BMD: {
    code: '48809-8',
    display: 'Δυστροφινοπάθεια (DMD/BMD)',
    category: 'POSTNATAL',
  },
  CPTII: {
    code: '53198-8',
    display: 'CPTII (παραλλαγή c.338C>T)',
    category: 'POSTNATAL',
  },
  SMA: {
    code: '50617-5',
    display: 'Νωτιαία μυϊκή ατροφία (SMA) τύπου Ι, ΙΙ, ΙΙΙ',
    category: 'POSTNATAL',
  },
  DM1: {
    code: '35351-9',
    display: 'Μυοτονική δυστροφία 1 (DM1)',
    category: 'POSTNATAL',
  },
  DM2: {
    code: '35353-5',
    display: 'Μυοτονική δυστροφία 2 (DM2/PROMM)',
    category: 'POSTNATAL',
  },
  OPMD: {
    code: '53200-2',
    display: 'Οφθαλμο-φαρυγγική μυϊκή δυστροφία',
    category: 'POSTNATAL',
  },
  FRAGILE_X: {
    code: '38530-8',
    display: 'Σύνδρομο εύθραυστου Χ',
    category: 'POSTNATAL',
  },
  FXTAS: { code: '46767-0', display: 'FXTAS', category: 'POSTNATAL' },
  FXPOI_PCR: { code: '50622-5', display: 'FXPOI (PCR)', category: 'POSTNATAL' },
  FXPOI_TRIPLET: {
    code: '50623-3',
    display: 'FXPOI (TRIPLET PCR)',
    category: 'POSTNATAL',
  },
  PRADER_WILLI: {
    code: '42945-6',
    display: 'Σύνδρομο Prader Willi',
    category: 'POSTNATAL',
  },
  ANGELMAN: {
    code: '42810-2',
    display: 'Σύνδρομο Angelman',
    category: 'POSTNATAL',
  },
  RETT_MECP2_SEQ: {
    code: '51963-7',
    display: 'Σύνδρομο Rett (MECP2 SEQ)',
    category: 'POSTNATAL',
  },
  RETT_MECP2_MLPA: {
    code: '62352-1',
    display: 'Σύνδρομο Rett (MECP2 MLPA)',
    category: 'POSTNATAL',
  },
  RETT_FOXG1: {
    code: '92818-1',
    display: 'Σύνδρομο Rett (FOXG1)',
    category: 'POSTNATAL',
  },
  EPILEPTIC_POLG1: {
    code: '53197-0',
    display: 'Επιληπτική εγκεφαλοπάθεια (POLG1)',
    category: 'POSTNATAL',
  },
  EPILEPTIC_ARX: {
    code: '48807-2',
    display: 'Επιληπτική εγκεφαλοπάθεια (ARX)',
    category: 'POSTNATAL',
  },
  DSD_AR: {
    code: '48806-4',
    display: 'Διαταραχή διαφοροποίησης φύλου (AR)',
    category: 'POSTNATAL',
  },
  DSD_SRY: {
    code: '55208-3',
    display: 'Διαταραχή διαφοροποίησης φύλου (SRY)',
    category: 'POSTNATAL',
  },
  DSD_AZF: {
    code: '42816-9',
    display: 'Διαταραχή διαφοροποίησης φύλου (AZF microdeletions)',
    category: 'POSTNATAL',
  },
  DSD_MLPA: {
    code: '55209-1',
    display:
      'Διαταραχή διαφοροποίησης φύλου (MLPA NROB1, DAX1, SOX9, SRY, WNT4, NR5A1)',
    category: 'POSTNATAL',
  },
  FABRY: { code: '42906-8', display: 'Νόσος Fabry', category: 'POSTNATAL' },
  ACHONDROPLASIA: {
    code: '50627-4',
    display: 'Αχονδροπλασία (FGFR3, c.1138G>A, c.1138G>C)',
    category: 'POSTNATAL',
  },
  SILVER_RUSSELL_CHR11: {
    code: '94229-2',
    display: 'Σύνδρομο Silver-Russell (MS-MLPA CHR 11)',
    category: 'POSTNATAL',
  },
  SILVER_RUSSELL_CHR7: {
    code: '94228-4',
    display: 'Σύνδρομο Silver-Russell (MS-MLPA CHR 7)',
    category: 'POSTNATAL',
  },
  BECKWITH_WIEDEMANN_CHR11: {
    code: '94056-9',
    display: 'Σύνδρομο Beckwith-Wiedemann (MS-MLPA CHR 11)',
    category: 'POSTNATAL',
  },
  BECKWITH_WIEDEMANN_CDKN1C: {
    code: '51682-3',
    display: 'Σύνδρομο Beckwith-Wiedemann (CDKN1C)',
    category: 'POSTNATAL',
  },

  // Mitochondrial tests
  MELAS: { code: '54404-2', display: 'MELAS', category: 'MITOCHONDRIAL' },
  LEIGH: { code: '54372-1', display: 'LEIGH', category: 'MITOCHONDRIAL' },
  NARP: { code: '54405-9', display: 'NARP', category: 'MITOCHONDRIAL' },
  MERRF: { code: '54375-4', display: 'MERRF', category: 'MITOCHONDRIAL' },
  LEBER: { code: '54373-9', display: 'LEBER', category: 'MITOCHONDRIAL' },
  CPEO: { code: '54406-7', display: 'CPEO', category: 'MITOCHONDRIAL' },
  KEARNS_SAYRE: {
    code: '54407-5',
    display: 'KEARNS SAYRE',
    category: 'MITOCHONDRIAL',
  },
  MITOCHONDRIAL_MYOPATHY: {
    code: '54408-3',
    display: 'Μιτοχονδριακή μυοπάθεια',
    category: 'MITOCHONDRIAL',
  },

  // Prenatal tests
  PRENATAL_KARYOTYPE: {
    code: '11556-7',
    display: 'Προγεννητικός κλασσικός καρυότυπος',
    category: 'PRENATAL',
  },
  PRENATAL_MOLECULAR_KARYOTYPE: {
    code: '69547-8',
    display: 'Προγεννητικός μοριακός καρυότυπος',
    category: 'PRENATAL',
  },
  PRENATAL_CUSTOM: {
    code: '69548-7',
    display: 'Στοχευμένη προγεννητική διάγνωση (custom)',
    category: 'PRENATAL',
  },

  // Preimplantation tests
  PGT_A: {
    code: '92822-3',
    display: 'Προεμφυτευτικός Γενετικός Έλεγχος για Ανευπλοειδίες (PGT-A)',
    category: 'PREIMPLANTATION',
  },
  PGT_M: {
    code: '92823-1',
    display:
      'Προεμφυτευτικός Γενετικός Έλεγχος για Μονογονιδιακά Νοσήματα (PGT-M)',
    category: 'PREIMPLANTATION',
  },
  PGT_SR: {
    code: '92824-9',
    display:
      'Προεμφυτευτικός Γενετικός Έλεγχος για Δομικές Ανακατατάξεις (PGT-SR)',
    category: 'PREIMPLANTATION',
  },
} as const;

export const SAMPLE_TYPES = [
  {
    code: '445295009',
    label: 'Περιφερικό αίμα σε EDTA',
    system: SYSTEMS.SNOMED,
    shortCode: 'BLD',
  },
  {
    code: '838971000000104',
    label: 'Περιφερικό αίμα σε ηπαρίνη',
    system: SYSTEMS.SNOMED,
    shortCode: 'HEP',
  },
  {
    code: '77012006',
    label: 'Αμνιακό υγρό',
    system: SYSTEMS.SNOMED,
    shortCode: 'AMN',
  },
  {
    code: '258565009',
    label: 'Χοριακές λάχνες',
    system: SYSTEMS.SNOMED,
    shortCode: 'CVS',
  },
  {
    code: '258564008',
    label: 'Επίχρισμα παρειάς',
    system: SYSTEMS.SNOMED,
    shortCode: 'BCK',
  },
  {
    code: '240977001',
    label: 'Βιοψία δέρματος',
    system: SYSTEMS.SNOMED,
    shortCode: 'SKN',
  },
  {
    code: '258428005',
    label: 'Προϊόν αποβολής',
    system: SYSTEMS.SNOMED,
    shortCode: 'POC',
  },
  {
    code: '441652008',
    label: 'Δείγμα ιστού σε παραφίνη (FFPE)',
    system: SYSTEMS.SNOMED,
    shortCode: 'FFP',
  },
  {
    code: '399411006',
    label: 'Βιοψία τροφοβλάστης',
    system: SYSTEMS.SNOMED,
    shortCode: 'BRB',
  },
  {
    code: '258421004',
    label: 'Βιοψία βλαστοκύστης',
    system: SYSTEMS.SNOMED,
    shortCode: 'BLS',
  },
  {
    code: '123038009',
    label: 'Custom sample',
    system: SYSTEMS.SNOMED,
    shortCode: 'CST',
  },
] as const;

// Extract the sample type codes as a union type
export type SampleTypeValue = (typeof SAMPLE_TYPES)[number]['code'];

export const CLINICAL_SIGNIFICANCE_CODES = {
  PATHOGENIC: { code: 'LA6668-3', display: 'Pathogenic', short: 'P' },
  LIKELY_PATHOGENIC: {
    code: 'LA26332-9',
    display: 'Likely pathogenic',
    short: 'LP',
  },
  UNCERTAIN_SIGNIFICANCE: {
    code: 'LA6675-8',
    display: 'Uncertain significance',
    short: 'VUS',
  },
  LIKELY_BENIGN: { code: 'LA26333-7', display: 'Likely benign', short: 'LB' },
  BENIGN: { code: 'LA6669-1', display: 'Benign', short: 'B' },
} as const;

export const TEST_RESULT_CODES = {
  POSITIVE: { code: '10828004', display: 'Positive' },
  NEGATIVE: { code: '260385009', display: 'Negative' },
  INCONCLUSIVE: { code: '42425007', display: 'Inconclusive' },
} as const;

export const ZYGOSITY_OPTIONS = [
  { value: 'Het', label: 'Heterozygous' },
  { value: 'Hom', label: 'Homozygous' },
  { value: 'Hem', label: 'Hemizygous' },
] as const;
